import { PageTypes, getPageType } from 'utils/pageTypes';
import { openPopup } from 'utils/openPopup';
import type { PopupOptions } from 'utils/openPopup';
import { openTab } from './openTab';
import { isRunningAsPWA } from 'modules/analytics/util';
import { NavigateFunction } from 'react-router-dom';
import { AppRoutePath } from 'config/constants';
import {
  dispatchExtensionEvent,
  transitionRouteEvent,
} from 'domains/extension/extensionEventCreators';
import { ExtWindowName } from 'hooks/useExtensionState';

type OpenViewerArguments = {
  to: string;
  navigate?: NavigateFunction;
  openedViewerIds: Set<string>;
  isReporterOpen: boolean;
  isWorklistOpen: boolean;
  isSearchOpen: boolean;
  isJacketOpen: boolean;
  isAISandboxOpen: boolean;
  viewerIndex: string;
  getPopupOptions: (url: string) => PopupOptions;
  pageType: PageTypes;
};

export const openWindow = ({
  to,
  navigate,
  openedViewerIds,
  isReporterOpen,
  isWorklistOpen,
  isSearchOpen,
  isJacketOpen,
  isAISandboxOpen,
  viewerIndex,
  getPopupOptions,
  pageType,
}: OpenViewerArguments): void => {
  const currentPageType = getPageType(window.location.pathname);
  const isCurrentlyOnWorklistTypePage = currentPageType === PageTypes.WORKLIST;

  const shouldOpenViewerIndex = pageType === PageTypes.VIEWER && !openedViewerIds.has(viewerIndex);
  const shouldOpenReporter = pageType === PageTypes.REPORTER && !isReporterOpen;
  const shouldOpenProductPortal = pageType === PageTypes.PRODUCTPORTAL;
  const shouldOpenWorklist =
    pageType === PageTypes.WORKLIST &&
    !isWorklistOpen &&
    !to.includes(AppRoutePath.WORKLIST_SEARCH);
  const shouldOpenSearch = to.includes(AppRoutePath.WORKLIST_SEARCH) && !isSearchOpen;
  const shouldOpenJacket = pageType === PageTypes.PATIENT_JACKET && !isJacketOpen;
  const shouldOpenAISandbox = pageType === PageTypes.AI_SANDBOX && !isAISandboxOpen;

  // If Worklist or Search is already open and the user clicks the other in the Navbar,
  // the de-dupe logic will close the new window without transitioning the existing window to the other page.
  // To fix this, we explicitly transition the existing Worklist window to the new page.
  if (
    pageType === PageTypes.WORKLIST &&
    (isWorklistOpen || isSearchOpen) &&
    !isCurrentlyOnWorklistTypePage
  ) {
    dispatchExtensionEvent(
      transitionRouteEvent({ target: ExtWindowName.Worklist, path: to, focus: true })
    );
    return;
  }

  if (isCurrentlyOnWorklistTypePage && pageType === PageTypes.WORKLIST && navigate !== undefined) {
    // If we're on a worklist-type page and navigating to another worklist-type page,
    // navigate in the same window
    navigate(to);
  } else if (shouldOpenWorklist || shouldOpenSearch) {
    const isRunningAsStandaloneApp = isRunningAsPWA();

    // If the app is running as a standalone app we don't want to open the worklist in a
    // normal tab but rather in a popup, that will look like yet another standalone window
    if (isRunningAsStandaloneApp) {
      openPopup(to, getPopupOptions(to));
    } else {
      openTab(to, getPopupOptions(to));
    }
  } else if (
    shouldOpenViewerIndex ||
    shouldOpenReporter ||
    shouldOpenProductPortal ||
    shouldOpenJacket ||
    shouldOpenAISandbox
  ) {
    const url = to.replace('/viewer/0', `/viewer/${viewerIndex}`);
    openPopup(url, getPopupOptions(url));
  }
};
